import { useMemo } from "react";
import type {
  MediaMetadata,
  PlatformRequirements,
} from "../components/brandDetails/tabs/creator-studio/platforms/types";

const REQUIREMENTS: PlatformRequirements = {
  maxImages: 10,
  maxVideos: 1,
  imageFormats: "JPEG, PNG",
  videoFormats: "MP4, MOV",
  maxImageSize: "8 MB",
  maxVideoSize: "300 MB (Feed), 100 MB (Reels/Stories)",
  videoDuration: "60 sec (Reels), 15 min (Feed)",
  recommended: "1080 × 1350 px (4:5)",
  minDimensions: "320 × 320 px",
  maxDimensions: "1936 × 1936 px",
  aspectRatios: "4:5 (portrait), 1:1 (square), 1.91:1 (landscape)",
  minWidth: 320,
  minHeight: 320,
  maxWidth: 1936,
  maxHeight: 1936,
  allowedAspectRatios: [{ min: 0.8, max: 1.91 }],
  note: "Media required - no text-only posts. Aspect ratio must be between 0.8 (4:5) and 1.91 (landscape). Images outside this range must be posted as Stories.",
};

export interface InstagramFormValidation {
  imageCountValid: boolean | null;
  imageSizeValid: boolean | null;
  dimensionsValid: boolean | null;
  aspectRatiosValid: boolean | null;
  videoCountValid: boolean | null;
  videoDurationValid: boolean | null;
}

export function useInstagramForm(
  mediaMetadata: MediaMetadata[],
  isStory?: boolean
) {
  const maxImages =
    typeof REQUIREMENTS.maxImages === "number" ? REQUIREMENTS.maxImages : 10;

  const validation = useMemo((): InstagramFormValidation => {
    const enabledMedia = mediaMetadata.slice(0, maxImages);

    if (enabledMedia.length === 0) {
      return {
        imageCountValid: null,
        imageSizeValid: null,
        dimensionsValid: null,
        aspectRatiosValid: null,
        videoCountValid: null,
        videoDurationValid: null,
      };
    }

    const images = enabledMedia.filter((m) => m.type === "image");
    const videos = enabledMedia.filter((m) => m.type === "video");

    const imageCountValid =
      images.length === 0 ? null : images.length <= maxImages;

    const maxImageSizeBytes = 8 * 1024 * 1024;
    const imageSizeValid =
      images.length === 0
        ? null
        : images.every((img) => img.size <= maxImageSizeBytes);

    const dimensionsValid =
      images.length === 0
        ? null
        : images.every(
            (img) =>
              img.width >= (REQUIREMENTS.minWidth ?? 0) &&
              img.height >= (REQUIREMENTS.minHeight ?? 0) &&
              img.width <= (REQUIREMENTS.maxWidth ?? Infinity) &&
              img.height <= (REQUIREMENTS.maxHeight ?? Infinity)
          );

    const aspectRatiosValid =
      isStory || images.length === 0
        ? isStory
          ? true
          : null
        : images.every((img) => {
            if (!REQUIREMENTS.allowedAspectRatios) return true;
            return REQUIREMENTS.allowedAspectRatios.some(
              (range) =>
                img.aspectRatio >= range.min && img.aspectRatio <= range.max
            );
          });

    const maxVideos =
      typeof REQUIREMENTS.maxVideos === "number"
        ? REQUIREMENTS.maxVideos
        : Infinity;
    const videoCountValid =
      videos.length === 0 ? null : videos.length <= maxVideos;

    const videoDurationValid =
      videos.length === 0
        ? null
        : videos.every((video) => (video.duration ?? 0) <= 900);

    return {
      imageCountValid,
      imageSizeValid,
      dimensionsValid,
      aspectRatiosValid,
      videoCountValid,
      videoDurationValid,
    };
  }, [mediaMetadata, maxImages, isStory]);

  return { validation, requirements: REQUIREMENTS };
}
